import React, { useCallback, useMemo, useRef } from 'react';
import Filter from '@/pages/dashboard/components/NewFilter';
import { setBtnPointLog } from '@/pages/dashboard/utils/utils';
import { formatDateFromFilterValue } from "@/pages/dashboard/utils/formatDate";
import { renderAppVersion, renderLanguage, renderMemory, renderNetWorkType, renderStep, renderStoreChannel } from './FilterList';
import { useFilterData } from './hooks';
import { FORM_MAP } from '../../utils/_config/constant';
import { getLocale, shallowEqual, useIntl, useSelector } from 'umi';
import { POE_DEMO_GAME_CODE_LIST } from '../../_config/constant';
import { getDateDefault } from '../../utils/dateUtils';
import { getMediaChannelIcon, mediaChannelMap } from '@/pages/home/_config/mediaChannal';
import Default from '@/pages/home/assets/svg/dashboard/default.svg';

// 筛选器路径
const FILTER_PATH_MOBILE = '/funnel/registerFunnel';
const FILTER_PATH_PC = '/funnel/pc/registerFunnel';
const FILTER_PATH_STEAM = '/funnel/steam/registerFunnel';
const FILTER_PATH_EPIC = '/funnel/epic/registerFunnel';
const FILTER_PATH_PS = '/funnel/ps/registerFunnel';
const FILTER_PATH_XBOX = '/funnel/xbox/registerFunnel';

/**
 *
 * @param props
 * @param props:filterConfirm 筛选器确认函数
 * @param props:filterReset 筛选器重置函数
 * @param props:filterInit 初始化函数，会向外部传递初始化后的筛选器值
 * @returns
 */

// FunnelFilter
const FunnelFilter = (props: any) => {
  const { filterConfirm, filterReset, filterInit, buttonLogConfig, openSticky = false, getSelectorData, hasAddInput, pageType, defaultSelect } = props;
  const pathMap: any = {
    'mobile': FILTER_PATH_MOBILE,
    'pc': FILTER_PATH_PC,
    'steam': FILTER_PATH_STEAM,
    'epic': FILTER_PATH_EPIC,
    'ps': FILTER_PATH_PS,
    'xbox': FILTER_PATH_XBOX
  };
  const PATH = pathMap[pageType];
  const filterValueRef = useRef<any>(null); // 筛选器值，此处不用state，更新formValue同样会触发组件更新
  const { loading, selectorSchemeList, getOutSelectorSchemeList, selector } = useFilterData(pageType, PATH);
  const { formatMessage } = useIntl();

  const { stage_list: stageList = [], default_stage_list, icon_stage_list, latestDate } = selector || {};
  const defaultStage = default_stage_list?.map((item: any) => item?.stage_id) || [];
  const defaultIconStage = icon_stage_list?.map((item: any) => item?.stage_id) || [];
  const {gameCode, timeZone, dataFrame} = useSelector((state: any) => ({
    gameCode: state.dashboard?.currentGame?.game_code,
    dataFrame: state.dashboard?.currentGame?.data_frame,
    timeZone: parseInt(state?.dashboardSetting?.gameConfig?.time_zone || '', 10) || 0
  }), shallowEqual);
  // 设置inputList
  const inputList = useMemo(() => {
    if (loading) return [];
    const default_inputList = [
      {
        type: 'rangeDaily',
        key: 'rangeTime',
        default: getDateDefault({ 
          timeType: 'day', 
          timeZone, 
          // 【PC端+经分】【优化2.2上线】漏斗页面默认日期调整
          startNum: dataFrame === 'casual' ? 15 : 14, 
          endNum: dataFrame === 'casual' ? 2 : 1 
        }),
      },
      {
        key: 'region',
        dataKey: 'region',
        default: {
          region_type: 'market',
          markets: ["255"],
          regions: []
        },
        name_cn: '区域',
        name_en: 'Region',
      },
      {
        key: 'channel',
        dataKey: 'channel',
        default: ["255"],
        name_cn: '渠道',
        name_en: 'Channel',
      },
      {
        key: 'system',
        dataKey: 'system',
        default: ["255"],
        name_cn: '平台',
        name_en: 'Platform',
      },
      {
        key: 'lang',
        dataKey: 'lang',
        default: ["255"],
        render: renderLanguage,
        name_cn: '语言',
        name_en: 'Language',
      },
      // 需求规定默认255
      {
        key: 'appVersion',
        dataKey: 'app_version',
        default: ['255'],
        formatMessage,
        render: renderAppVersion,
        name_cn: 'App版本',
        name_en: 'APP Version',
      },
      {
        key: 'networkType',
        dataKey: 'network',
        default: ['255'],
        render: renderNetWorkType,
        name_cn: '网络类型',
        name_en: 'NetWork Type',
      },
      // 需求规定默认255
      {
        key: 'memory',
        dataKey: 'memory',
        default: ['255'],
        formatMessage,
        render: renderMemory,
        name_cn: '内存',
        name_en: 'Memory',
      },
    ];
    // 定制化Step步骤筛选器(固定显示，不走配置逻辑)
    const stepSelect = {
      key: 'stage',
      dataKey: 'stage',
      default: defaultStage,
      defaultIconStage,
      render: renderStep,
      name_cn: '步骤',
      name_en: 'Step',
    };
    // 后端未配置使用默认的筛选器
    if (!defaultSelect) {
      default_inputList?.push(stepSelect);
      return default_inputList;
    }

    const storeChannelSelect = {
      key: 'storeChannel',
      dataKey: 'store_channel',
      default: ['255'],
      render: renderStoreChannel
    };
    const mediaChannelSelect = {
      key: 'ua_network',
      dataKey: 'ua_network',
      default: ['255'],
    };
    const campaignSelect = {
      key: 'campaign',
      dataKey: 'campaign',
      default: ['255'],
    };
    const abTestSelect = {
      key: 'ab_test',
      dataKey: 'ab_test',
      default: ['255'],
    };
    if (hasAddInput) default_inputList?.push(storeChannelSelect);
    if (dataFrame === 'casual') {
      default_inputList?.push(mediaChannelSelect);
      default_inputList?.push(campaignSelect);
      default_inputList?.push(abTestSelect);
    }
    default_inputList?.push(stepSelect);
    // 筛选器私有化的处理显示隐藏
    const list: any = [];
    default_inputList?.map((item: any) => {
      const input = defaultSelect?.find((v: any) => v?.key === item?.dataKey);

      // 时间和步骤筛选器默认展示
      if (item?.key === 'rangeTime' || item?.key === 'stage') {
        list?.push(item);
      } else if (input && input?.visible !== 0) {
        if (input?.key === 'region') {
          list?.push({
            ...item, default: {
              markets: input?.default,
              region_type: "market",
              regions: []
            },
            name_cn: input?.name_cn || item?.name_cn,
            name_en: input?.name_en || item?.name_en,
          });
        } else {
          list?.push({ ...item, default: input?.default, visible: input?.visible, view_type: input?.view_type,
            name_cn: input?.name_cn || item?.name_cn,
            name_en: input?.name_en || item?.name_en, });
        }
      }
    });
    return list;
  }, [loading, getLocale(), defaultSelect, hasAddInput]);

  const isPoeDemo = POE_DEMO_GAME_CODE_LIST?.includes(gameCode);
  const isRegisterFunnel = window?.location.href?.includes('funnel/registerFunnel');
  const isPoeFunnel = isPoeDemo && isRegisterFunnel;

  // 筛选器私有化的处理隐藏筛选器的值
  const noVisibleValue = useMemo(() => {
    const obj: any = {};
    const keyMap: any = {
      app_version: 'appVersion',
      network: 'networkType',
      store_channel: 'storeChannel',
    };
    defaultSelect?.map((item: any) => {
      if (item?.visible === 0) {
        if (item?.key === 'region') {
          obj[keyMap?.[item?.key] || item?.key] = {
            markets: item?.default,
            region_type: "market",
            regions: []
          };
        } else {
          obj[keyMap?.[item?.key] || item?.key] = item?.default;
        }
      }
    });
    return obj;
  }, [defaultSelect]);

  // 私有化处理，抹平新旧筛选器的差距
  const onInit = useCallback((value, inputList, isHaveExtendFIlter) => {
    filterValueRef.current = { ...value, ...noVisibleValue };
    // todo 锁定状态下如果锁定时间跟默认时间相同，是否需要单独处理，设置diff为false？
    filterInit({
      filterValue: formatDateFromFilterValue({ ...value, ...noVisibleValue }),
      newFilter: true,
      [FORM_MAP.RANGE_TIME_DIFF]: !isHaveExtendFIlter,
      stageList,
      defaultStage,
      defaultIconStage,
    });
    getSelectorData(selector);
  }, [filterInit, noVisibleValue, selector]);

  const onConfirm = useCallback((value) => {
    filterValueRef.current = { ...value, ...noVisibleValue };
    const obj: any = {
      filterValue: formatDateFromFilterValue({ ...value, ...noVisibleValue }),
      newFilter: true,
      [FORM_MAP.RANGE_TIME_DIFF]: false,
      stageList,
      defaultStage,
      defaultIconStage,
    };
    filterConfirm(obj);
    // 打点，沟通后打点操作放在业务代码的confirm和reset中，不封装在新版本筛选器中
    setBtnPointLog(buttonLogConfig, obj?.filterValue);
  }, [filterConfirm, selector]);

  const onReset = useCallback((value) => {
    filterValueRef.current = { ...value, ...noVisibleValue };
    const obj = {
      filterValue: formatDateFromFilterValue({ ...value, ...noVisibleValue }),
      newFilter: true,
      [FORM_MAP.RANGE_TIME_DIFF]: true,
      stageList,
      defaultStage,
      defaultIconStage,
    };
    filterReset(obj);
    // 打点，沟通后打点操作放在业务代码的confirm和reset中，不封装在新版本筛选器中
    setBtnPointLog(buttonLogConfig, obj?.filterValue, true);
  }, [filterReset, selector]);

  const newSelector = useMemo(() => {
    const newSelector = {
      ...selector,
      ua_network: (selector?.ua_network_list || [])?.map((item: { ua_network_id: string; ua_network_type: string; ua_network_name_ch: string; ua_network_name_en: string; }) => {
        const channelIcon = getMediaChannelIcon(item?.ua_network_name_en || item?.ua_network_id || '');
        return {
          ...item,
          code: item?.ua_network_id,
          type: item?.ua_network_type,
          name_ch: item?.ua_network_name_ch,
          name_en: item?.ua_network_name_en,
          icon: channelIcon === mediaChannelMap._default ? Default : channelIcon,
        };
      }) || [],
      campaign: (selector?.campaign_list || [])?.map((item: { campaign_id: string; campaign_name_ch: string; campaign_name_en: string; }) => {
        return {
          ...item,
          code: item?.campaign_id,
          name_ch: item?.campaign_name_ch,
          name_en: item?.campaign_name_en,
        };
      }) || [],
      ab_test: (selector?.ab_test_list || [])?.map((item: { ab_test_id: string; ab_test_name_ch: string; ab_test_name_en: string; }) => {
        return {
          ...item,
          code: item?.ab_test_id,
          name_ch: item?.ab_test_name_ch,
          name_en: item?.ab_test_name_en,
        };
      }) || [],
    };
    return newSelector;
  },[selector]);

  return < Filter
    openSticky={isPoeFunnel ? false : openSticky}
    path={PATH}
    isHaveFilterSave={!isPoeFunnel}
    outLatestDate={latestDate}
    value={filterValueRef.current}
    latestDateType={''}
    inputList={inputList}
    onInit={onInit}
    onConfirm={onConfirm}
    onReset={onReset}
    isHaveFilterCache={false}
    loading={loading}
    useOutInput={true}
    outSelector={newSelector}
    outSelectorSchemeList={selectorSchemeList}
    getOutSelectorSchemeList={getOutSelectorSchemeList}
  />;
};

export default FunnelFilter;
